// src/app/features/claims/store/claims.effects.ts
//
// WHY a local inFlight set instead of checking store state:
// NgRx reducers always run BEFORE effects for the same action. If the reducer
// sets a "loading" flag when Load is dispatched, the effect sees it already set
// and would return EMPTY — so no HTTP call would ever fire. Instead:
//   • Static/cached data  → cache TTL check (isCacheValid) prevents re-fetching
//   • Search effects      → switchMap auto-cancels the previous pending call
//   • Keyed per-ID data   → local Set<string> in this class tracks truly in-flight calls

import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, EMPTY, forkJoin } from 'rxjs';
import { switchMap, map, catchError, withLatestFrom, finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { ClaimsApiService } from '../services/claims-api.service';
import { isCacheValid, TTL } from './claims.state';
import {
  TransakcionetLlojetActions,
  ShendetLlojetActions,
  AksidentLlojetActions,
  AksidentNenLlojetActions,
  BankatActions,
  TrajtuesitActions,
  AdministratoretActions,
  ShtetetActions,
  PerfitimetPerPoliceActions,
  KartelaSearchActions,
  KlinikaSearchActions,
  PerfituesiSearchActions,
  XhirollogariteActions,
  ExchangeRateActions,
  DemitShActions,
  IsiguruariSearchActions,
  PoliceSearchActions,
  KartelaSearchDemActions,
  RapDemitShActions,
  DemiShDetaletActions,
  DemiShVizitatActions,
  DemeShendetTotalActions,
  ValiditetiKartelesActions,
  TransakcionetPerVisitActions,
  ClaimVisitsRestActions,
  FleteTrajtimShendetActions,
  KerkesaDemitShendetActions,
  NjoftimPerLikuidimShActions,
  CvSessionByVisitActions,
  CvAnalysesByVisitActions,
  CvFilesByVisitActions,
  CvSessionsByClaimHealthActions,
  CvLocalAnalysesActions,
  CvMySessionsActions,
  RapDemitShOnlineActions,
  DokTjeraActions,
  KuponaFiskalActions,
  CompanyBankAccountsActions,
  IsiguruaritActions,
  PaymentsSh1Actions,
  PaymentsSh5Actions,
  HistorikPerKarteleActions,
} from './claims.actions';
import {
  selectTransakcionetLlojet,
  selectShendetLlojet,
  selectAksidentLlojet,
  selectBankat,
  selectTrajtuesit,
  selectAdministratoret,
  selectShtetetMap,
  selectPerfitimetMap,
  selectAksidentNenLlojetAll,
  selectXhirollogariteMap,
  selectExchangeRateFor,
  selectClaimDetailMap,
  selectClaimVisitsMap,
  selectDemeShendetTotalMap,
  selectValiditetiKartelesMap,
  selectTransakcionetPerVisitMap,
  selectClaimVisitsRestMap,
  selectFleteTrajtimShendetMap,
  selectKerkesaDemitShendetMap,
  selectNjoftimPerLikuidimShMap,
  selectCvSessionByVisitMap,
  selectCvAnalysesByVisitMap,
  selectCvFilesByVisitMap,
  selectCvSessionsByClaimHealthMap,
  selectCvLocalAnalysesMap,
  selectCvMySessions,
  selectDokTjeraMap,
  selectKuponaFiskalMap,
  selectCompanyBankAccounts,
  selectIsiguruaritMap,
  selectHistorikPerKarteleMap,
} from './claims.selectors';

@Injectable()
export class ClaimsEffects {
  private readonly actions$   = inject(Actions);
  private readonly store      = inject(Store);
  private readonly api        = inject(ClaimsApiService);
  private readonly translate  = inject(TranslateService);

  /**
   * Local in-flight set — prevents duplicate HTTP calls for keyed data
   * (per-policy, per-person, per-type). Not stored in NgRx state because
   * the reducer runs before effects, making store-based checks impossible.
   */
  private readonly _inFlight = new Set<string>();

  private readError(err: unknown): string {
    const e = err as any;
    return e?.error?.message ?? e?.message ?? this.translate.instant('ERRORS.UNEXPECTED_DOT');
  }

  // ── TransakcionetLlojet ──────────────────────────────────────────────────
  loadTransakcioet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransakcionetLlojetActions.load),
      withLatestFrom(this.store.select(selectTransakcionetLlojet)),
      switchMap(([{ forceReload }, slice]) => {
        if (!forceReload && isCacheValid(slice.cache)) return EMPTY;
        return this.api.getTransakcionetLlojet().pipe(
          map(data => TransakcionetLlojetActions.success({ data })),
          catchError(err => of(TransakcionetLlojetActions.failure({ error: this.readError(err) }))),
        );
      }),
    )
  );

  // ── ShendetLlojet ────────────────────────────────────────────────────────
  loadShendetLlojet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShendetLlojetActions.load),
      withLatestFrom(this.store.select(selectShendetLlojet)),
      switchMap(([{ forceReload }, slice]) => {
        if (!forceReload && isCacheValid(slice.cache)) return EMPTY;
        return this.api.getPerfitimetShendetLlojet().pipe(
          map(data => ShendetLlojetActions.success({ data })),
          catchError(err => of(ShendetLlojetActions.failure({ error: this.readError(err) }))),
        );
      }),
    )
  );

  // ── AksidentLlojet ───────────────────────────────────────────────────────
  loadAksidentLlojet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AksidentLlojetActions.load),
      withLatestFrom(this.store.select(selectAksidentLlojet)),
      switchMap(([{ forceReload }, slice]) => {
        if (!forceReload && isCacheValid(slice.cache)) return EMPTY;
        return this.api.getPerfitimetAksidentLlojet().pipe(
          map(data => AksidentLlojetActions.success({ data })),
          catchError(err => of(AksidentLlojetActions.failure({ error: this.readError(err) }))),
        );
      }),
    )
  );

  // ── AksidentNenLlojet ────────────────────────────────────────────────────
  loadAksidentNenLlojet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AksidentNenLlojetActions.load),
      withLatestFrom(this.store.select(selectAksidentNenLlojetAll)),
      switchMap(([{ btypeId, forceReload }, nenLlojetMap]) => {
        const slice = nenLlojetMap[btypeId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `nen:${btypeId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getPerfitimetAksidentNenLlojet(btypeId).pipe(
          map(data => AksidentNenLlojetActions.success({ btypeId, data })),
          catchError(err => of(AksidentNenLlojetActions.failure({ btypeId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── Bankat ───────────────────────────────────────────────────────────────
  loadBankat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BankatActions.load),
      withLatestFrom(this.store.select(selectBankat)),
      switchMap(([{ forceReload }, slice]) => {
        if (!forceReload && isCacheValid(slice.cache)) return EMPTY;
        return this.api.getBankat().pipe(
          map(data => BankatActions.success({ data })),
          catchError(err => of(BankatActions.failure({ error: this.readError(err) }))),
        );
      }),
    )
  );

  // ── Trajtuesit ───────────────────────────────────────────────────────────
  loadTrajtuesit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TrajtuesitActions.load),
      withLatestFrom(this.store.select(selectTrajtuesit)),
      switchMap(([{ forceReload }, slice]) => {
        if (!forceReload && isCacheValid(slice.cache)) return EMPTY;
        return this.api.getTrajtuesitShendet().pipe(
          map(data => TrajtuesitActions.success({ data })),
          catchError(err => of(TrajtuesitActions.failure({ error: this.readError(err) }))),
        );
      }),
    )
  );

  // ── Administratoret ──────────────────────────────────────────────────────
  loadAdministratoret$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdministratoretActions.load),
      withLatestFrom(this.store.select(selectAdministratoret)),
      switchMap(([{ forceReload }, slice]) => {
        if (!forceReload && isCacheValid(slice.cache)) return EMPTY;
        return this.api.getAdministratoretShendet().pipe(
          map(data => AdministratoretActions.success({ data })),
          catchError(err => of(AdministratoretActions.failure({ error: this.readError(err) }))),
        );
      }),
    )
  );

  // ── Shtetet ──────────────────────────────────────────────────────────────
  loadShtetet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShtetetActions.load),
      withLatestFrom(this.store.select(selectShtetetMap)),
      switchMap(([{ policaId, forceReload }, shtetetMap]) => {
        const slice = shtetetMap[policaId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `shtet:${policaId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getShtetetShendet(policaId).pipe(
          map(data => ShtetetActions.success({ policaId, data })),
          catchError(err => of(ShtetetActions.failure({ policaId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── PerfitimetPerPolice ──────────────────────────────────────────────────
  loadPerfitimetPerPolice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PerfitimetPerPoliceActions.load),
      withLatestFrom(this.store.select(selectPerfitimetMap)),
      switchMap(([{ policaId, forceReload }, perfMap]) => {
        const slice = perfMap[policaId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `perf:${policaId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getPerfitimetPerPolice(policaId).pipe(
          map(data => PerfitimetPerPoliceActions.success({ policaId, data })),
          catchError(err => of(PerfitimetPerPoliceActions.failure({ policaId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── KartelaSearch ────────────────────────────────────────────────────────
  // switchMap cancels any previous pending search when a new one arrives.
  searchKartela$ = createEffect(() =>
    this.actions$.pipe(
      ofType(KartelaSearchActions.search),
      switchMap(({ bllojiKerkimit, sKerkimi }) =>
        this.api.searchKartela(bllojiKerkimit, sKerkimi).pipe(
          map(data => KartelaSearchActions.success({ data })),
          catchError(err => of(KartelaSearchActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── KlinikaSearch ────────────────────────────────────────────────────────
  searchKlinika$ = createEffect(() =>
    this.actions$.pipe(
      ofType(KlinikaSearchActions.search),
      switchMap(({ sEmri }) =>
        this.api.searchKlinika(sEmri).pipe(
          map(data => KlinikaSearchActions.success({ data })),
          catchError(err => of(KlinikaSearchActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── PerfituesiSearch ─────────────────────────────────────────────────────
  searchPerfituesi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PerfituesiSearchActions.search),
      switchMap(({ bllojiKerkimit, sKerkimi }) =>
        this.api.searchPerfituesi(bllojiKerkimit, sKerkimi).pipe(
          map(data => PerfituesiSearchActions.success({ data })),
          catchError(err => of(PerfituesiSearchActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── Xhirollogarite ───────────────────────────────────────────────────────
  loadXhirollogarite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(XhirollogariteActions.load),
      withLatestFrom(this.store.select(selectXhirollogariteMap)),
      switchMap(([{ personId, vetemAktive = true, forceReload }, xhiroMap]) => {
        const slice = xhiroMap[personId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `xhiro:${personId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getXhirollogarite(personId, vetemAktive).pipe(
          map(data => XhirollogariteActions.success({ personId, data })),
          catchError(err => of(XhirollogariteActions.failure({ personId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── ExchangeRate ─────────────────────────────────────────────────────────
  loadExchangeRate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExchangeRateActions.load),
      switchMap(({ currencyCode, forceReload }) =>
        this.store.select(selectExchangeRateFor(currencyCode)).pipe(
          switchMap(cached => {
            if (!forceReload && cached) return EMPTY;
            const key = `rate:${currencyCode}`;
            if (this._inFlight.has(key)) return EMPTY;
            this._inFlight.add(key);
            return this.api.getExchangeRate(currencyCode).pipe(
              map(({ rate, date }) => ExchangeRateActions.success({ currencyCode, rate, date })),
              catchError(err => of(ExchangeRateActions.failure({ currencyCode, error: this.readError(err) }))),
              finalize(() => this._inFlight.delete(key)),
            );
          }),
        )
      ),
    )
  );

  // ── DemitSh ──────────────────────────────────────────────────────────────
  //
  // Multiple requests can be dispatched in a single action (one per Llojisig the
  // user has access to — e.g. Grup=18 AND Familjar=19). The effect fires them in
  // parallel via forkJoin and merges the items + totals before dispatching a
  // single Success action. switchMap ensures only the latest search wins.
  searchDemitSh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DemitShActions.search),
      switchMap(({ requests }) => {
        if (!requests?.length) {
          return of(DemitShActions.success({ data: [], total: 0 }));
        }
        return forkJoin(requests.map(r => this.api.kerkimiDemitSh(r))).pipe(
          map(results => {
            const data  = results.flatMap(r => r.items);
            const total = results.reduce((sum, r) => sum + (r.total ?? 0), 0);
            return DemitShActions.success({ data, total });
          }),
          catchError(err => of(DemitShActions.failure({ error: this.readError(err) }))),
        );
      }),
    )
  );

  // ── IsiguruariSearch ─────────────────────────────────────────────────────
  searchIsiguruari$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IsiguruariSearchActions.search),
      switchMap(({ bllojiKerkimit, sKerkimi }) =>
        this.api.kerkimiDemeSipasIsig(bllojiKerkimit, sKerkimi).pipe(
          map(data => IsiguruariSearchActions.success({ data })),
          catchError(err => of(IsiguruariSearchActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── PoliceSearch ─────────────────────────────────────────────────────────
  searchPolice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PoliceSearchActions.search),
      switchMap(({ bllojisig, bllojiKerkimit, sKerkimi }) =>
        this.api.kerkimiDemeSipasNrPolices(bllojisig, bllojiKerkimit, sKerkimi).pipe(
          map(data => PoliceSearchActions.success({ data })),
          catchError(err => of(PoliceSearchActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── KartelaSearchDem ─────────────────────────────────────────────────────
  searchKartelaDem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(KartelaSearchDemActions.search),
      switchMap(({ bllojiKerkimit, sKerkimi }) =>
        this.api.kerkimiDemeSipasKarteles(bllojiKerkimit, sKerkimi).pipe(
          map(data => KartelaSearchDemActions.success({ data })),
          catchError(err => of(KartelaSearchDemActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── DemiShDetalet ────────────────────────────────────────────────────────
  loadDemiShDetalet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DemiShDetaletActions.load),
      withLatestFrom(this.store.select(selectClaimDetailMap)),
      switchMap(([{ claimId, forceReload }, detailMap]) => {
        const slice = detailMap[claimId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `detail:${claimId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getDemiShDetalet(claimId).pipe(
          map(data => DemiShDetaletActions.success({ claimId, data })),
          catchError(err => of(DemiShDetaletActions.failure({ claimId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── DemiShVizitat ────────────────────────────────────────────────────────
  loadDemiShVizitat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DemiShVizitatActions.load),
      withLatestFrom(this.store.select(selectClaimVisitsMap)),
      switchMap(([{ claimId, forceReload }, visitsMap]) => {
        const slice = visitsMap[claimId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `visits:${claimId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getDemiShVizitatDetalet(claimId).pipe(
          map(data => DemiShVizitatActions.success({ claimId, data })),
          catchError(err => of(DemiShVizitatActions.failure({ claimId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── RapDemitSh ───────────────────────────────────────────────────────────
  searchRapDemitSh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RapDemitShActions.search),
      switchMap(({ request }) =>
        this.api.rapDemitSh(request).pipe(
          map(res => RapDemitShActions.success({ data: res.rapDemitSh, total: res.totalRreshta })),
          catchError(err => of(RapDemitShActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── DemeShendetTotal ──────────────────────────────────────────────────────
  loadDemeShendetTotal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DemeShendetTotalActions.load),
      withLatestFrom(this.store.select(selectDemeShendetTotalMap)),
      switchMap(([{ userId, data1, data2, forceReload }, totalMap]) => {
        const slice = totalMap[userId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `demeTotal:${userId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getDemeShendetTotalPerUser(data1, data2, userId).pipe(
          map(data => DemeShendetTotalActions.success({ userId, data })),
          catchError(err => of(DemeShendetTotalActions.failure({ userId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── ValiditetiKarteles ────────────────────────────────────────────────────
  loadValiditetiKarteles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ValiditetiKartelesActions.load),
      withLatestFrom(this.store.select(selectValiditetiKartelesMap)),
      switchMap(([{ policaId, cardId, forceReload }, kartMap]) => {
        const key = `${policaId}_${cardId}`;
        const slice = kartMap[key];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const inFlightKey = `kartVal:${key}`;
        if (this._inFlight.has(inFlightKey)) return EMPTY;
        this._inFlight.add(inFlightKey);
        return this.api.getValiditetiKarteles(policaId, cardId).pipe(
          map(data => ValiditetiKartelesActions.success({ policaId, cardId, data })),
          catchError(err => of(ValiditetiKartelesActions.failure({ policaId, cardId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(inFlightKey)),
        );
      }),
    )
  );

  // ── TransakcionetPerVisit ─────────────────────────────────────────────────
  loadTransakcionetPerVisit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransakcionetPerVisitActions.load),
      withLatestFrom(this.store.select(selectTransakcionetPerVisitMap)),
      switchMap(([{ visitId, forceReload }, tranMap]) => {
        const slice = tranMap[visitId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `tran:${visitId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getTransakcionetPerVisit(visitId).pipe(
          map(data => TransakcionetPerVisitActions.success({ visitId, data })),
          catchError(err => of(TransakcionetPerVisitActions.failure({ visitId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── ClaimVisitsRest ───────────────────────────────────────────────────────
  loadClaimVisitsRest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClaimVisitsRestActions.load),
      withLatestFrom(this.store.select(selectClaimVisitsRestMap)),
      switchMap(([{ claimId, forceReload }, visitsMap]) => {
        const slice = visitsMap[claimId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `visitsRest:${claimId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getClaimVisitsRest(claimId).pipe(
          map(data => ClaimVisitsRestActions.success({ claimId, data })),
          catchError(err => of(ClaimVisitsRestActions.failure({ claimId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── FleteTrajtimShendet ───────────────────────────────────────────────────
  loadFleteTrajtimShendet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FleteTrajtimShendetActions.load),
      withLatestFrom(this.store.select(selectFleteTrajtimShendetMap)),
      switchMap(([{ claimId, cardId, forceReload }, fleteMap]) => {
        const key = `${claimId}_${cardId}`;
        const slice = fleteMap[key];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const inFlightKey = `flete:${key}`;
        if (this._inFlight.has(inFlightKey)) return EMPTY;
        this._inFlight.add(inFlightKey);
        return this.api.getFleteTrajtimShendet(claimId, cardId).pipe(
          map(data => FleteTrajtimShendetActions.success({ claimId, cardId, data })),
          catchError(err => of(FleteTrajtimShendetActions.failure({ claimId, cardId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(inFlightKey)),
        );
      }),
    )
  );

  // ── KerkesaDemitShendet ───────────────────────────────────────────────────
  loadKerkesaDemitShendet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(KerkesaDemitShendetActions.load),
      withLatestFrom(this.store.select(selectKerkesaDemitShendetMap)),
      switchMap(([{ claimId, forceReload }, kerkesaMap]) => {
        const slice = kerkesaMap[claimId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `kerkesa:${claimId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getKerkesaDemitShendet(claimId).pipe(
          map(data => KerkesaDemitShendetActions.success({ claimId, data })),
          catchError(err => of(KerkesaDemitShendetActions.failure({ claimId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── NjoftimPerLikuidimSh ──────────────────────────────────────────────────
  loadNjoftimPerLikuidimSh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NjoftimPerLikuidimShActions.load),
      withLatestFrom(this.store.select(selectNjoftimPerLikuidimShMap)),
      switchMap(([{ claimId, forceReload }, njoftimMap]) => {
        const slice = njoftimMap[claimId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `njoftim:${claimId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getNjoftimPerLikuidimSh(claimId).pipe(
          map(data => NjoftimPerLikuidimShActions.success({ claimId, data })),
          catchError(err => of(NjoftimPerLikuidimShActions.failure({ claimId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── CvSessionByVisit (keyed by visitId — single session) ─────────────────
  loadCvSessionByVisit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CvSessionByVisitActions.load),
      withLatestFrom(this.store.select(selectCvSessionByVisitMap)),
      switchMap(([{ visitId, forceReload }, cvVisitMap]) => {
        const slice = cvVisitMap[visitId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `cvVisit:${visitId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getSessionByVisit(visitId).pipe(
          map(data => CvSessionByVisitActions.success({ visitId, data })),
          catchError(err => of(CvSessionByVisitActions.failure({ visitId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── CvAnalysesByVisit (keyed by visitId) ─────────────────────────────────
  loadCvAnalysesByVisit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CvAnalysesByVisitActions.load),
      withLatestFrom(this.store.select(selectCvAnalysesByVisitMap)),
      switchMap(([{ visitId, forceReload }, cvAnalysesMap]) => {
        const slice = cvAnalysesMap[visitId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `cvAnalyses:${visitId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getAnalysesByVisit(visitId).pipe(
          map(data => CvAnalysesByVisitActions.success({ visitId, data })),
          catchError(err => of(CvAnalysesByVisitActions.failure({ visitId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── CvFilesByVisit (keyed by visitId) ────────────────────────────────────
  loadCvFilesByVisit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CvFilesByVisitActions.load),
      withLatestFrom(this.store.select(selectCvFilesByVisitMap)),
      switchMap(([{ visitId, forceReload }, cvFilesMap]) => {
        const slice = cvFilesMap[visitId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `cvFiles:${visitId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getFilesByVisit(visitId).pipe(
          map(data => CvFilesByVisitActions.success({ visitId, data })),
          catchError(err => of(CvFilesByVisitActions.failure({ visitId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── CvSessionsByClaimHealth (keyed by claimHealthId) ─────────────────────
  loadCvSessionsByClaimHealth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CvSessionsByClaimHealthActions.load),
      withLatestFrom(this.store.select(selectCvSessionsByClaimHealthMap)),
      switchMap(([{ claimHealthId, forceReload }, cvClaimHealthMap]) => {
        const slice = cvClaimHealthMap[claimHealthId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `cvClaimHealth:${claimHealthId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getSessionsByClaimHealth(claimHealthId).pipe(
          map(data => CvSessionsByClaimHealthActions.success({ claimHealthId, data })),
          catchError(err => of(CvSessionsByClaimHealthActions.failure({ claimHealthId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── CvLocalAnalyses (keyed by cvClaimId) ─────────────────────────────────
  loadCvLocalAnalyses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CvLocalAnalysesActions.load),
      withLatestFrom(this.store.select(selectCvLocalAnalysesMap)),
      switchMap(([{ cvClaimId, forceReload }, cvLocalMap]) => {
        const slice = cvLocalMap[cvClaimId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `cvLocalAnalyses:${cvClaimId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getLocalCvAnalyses(cvClaimId).pipe(
          map(data => CvLocalAnalysesActions.success({ cvClaimId, data })),
          catchError(err => of(CvLocalAnalysesActions.failure({ cvClaimId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── CvMySessions (current user — no key) ─────────────────────────────────
  loadCvMySessions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CvMySessionsActions.load),
      withLatestFrom(this.store.select(selectCvMySessions)),
      switchMap(([{ forceReload }, slice]) => {
        if (!forceReload && isCacheValid(slice.cache)) return EMPTY;
        const key = 'cvMySessions';
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getMyCvSessions().pipe(
          map(data => CvMySessionsActions.success({ data })),
          catchError(err => of(CvMySessionsActions.failure({ error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── RapDemitShOnline (search — switchMap cancels previous) ───────────────
  rapDemitShOnline$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RapDemitShOnlineActions.search),
      switchMap(({ filter }) =>
        this.api.getRapDemitShOnline(filter).pipe(
          map(data => RapDemitShOnlineActions.success({ data })),
          catchError(err => of(RapDemitShOnlineActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── DokTjera (keyed by raportimiId) ──────────────────────────────────────
  loadDokTjera$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DokTjeraActions.load),
      withLatestFrom(this.store.select(selectDokTjeraMap)),
      switchMap(([{ raportimiId, forceReload }, dokTjeraMap]) => {
        const slice = dokTjeraMap[raportimiId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `dokTjera:${raportimiId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getDokTjera(raportimiId).pipe(
          map(data => DokTjeraActions.success({ raportimiId, data })),
          catchError(err => of(DokTjeraActions.failure({ raportimiId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── KuponaFiskal (keyed by raportimiId) ──────────────────────────────────
  loadKuponaFiskal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(KuponaFiskalActions.load),
      withLatestFrom(this.store.select(selectKuponaFiskalMap)),
      switchMap(([{ raportimiId, forceReload }, kuponaMap]) => {
        const slice = kuponaMap[raportimiId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `kuponaFiskal:${raportimiId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getKuponaFiskal(raportimiId).pipe(
          map(data => KuponaFiskalActions.success({ raportimiId, data })),
          catchError(err => of(KuponaFiskalActions.failure({ raportimiId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── CompanyBankAccounts (static lookup) ──────────────────────────────────
  loadCompanyBankAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyBankAccountsActions.load),
      withLatestFrom(this.store.select(selectCompanyBankAccounts)),
      switchMap(([{ forceReload }, slice]) => {
        if (!forceReload && isCacheValid(slice.cache)) return EMPTY;
        return this.api.getCompanyBankAccounts().pipe(
          map(data => CompanyBankAccountsActions.success({ data })),
          catchError(err => of(CompanyBankAccountsActions.failure({ error: this.readError(err) }))),
        );
      }),
    )
  );

  // ── Isiguruarit (keyed by lloji 1|2|3) ───────────────────────────────────
  loadIsiguruarit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IsiguruaritActions.load),
      withLatestFrom(this.store.select(selectIsiguruaritMap)),
      switchMap(([{ lloji, forceReload }, isiguruaritMap]) => {
        const slice = isiguruaritMap[lloji];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `isiguruarit:${lloji}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getIsiguruarit(lloji).pipe(
          map(data => IsiguruaritActions.success({ lloji, data })),
          catchError(err => of(IsiguruaritActions.failure({ lloji, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── PaymentsSh1 (search — switchMap cancels previous) ────────────────────
  loadPaymentsSh1$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PaymentsSh1Actions.load),
      switchMap(({ request }) =>
        this.api.rapdemepageseSh1(request).pipe(
          map(data => PaymentsSh1Actions.success({ data })),
          catchError(err => of(PaymentsSh1Actions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── PaymentsSh5 (search — switchMap cancels previous) ────────────────────
  loadPaymentsSh5$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PaymentsSh5Actions.load),
      switchMap(({ request }) =>
        this.api.rapdemepageseSh5(request).pipe(
          map(data => PaymentsSh5Actions.success({ data })),
          catchError(err => of(PaymentsSh5Actions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── HistorikPerKartele (keyed by claimId) ────────────────────────────────
  loadHistorikPerKartele$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HistorikPerKarteleActions.load),
      withLatestFrom(this.store.select(selectHistorikPerKarteleMap)),
      switchMap(([{ claimId, cardNo, llojiSig, take, forceReload }, historikMap]) => {
        const slice = historikMap[claimId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `historik:${claimId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getHistorikPerKartele(cardNo, llojiSig, claimId, take ?? 5).pipe(
          map(data => HistorikPerKarteleActions.success({ claimId, data })),
          catchError(err => of(HistorikPerKarteleActions.failure({ claimId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );
}
